import _ from 'underscore';

import { Set } from '~commonInfra/utils/Set';

import { Messages } from '../../../codegen/Localize';
import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues
} from '../../../commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '../../../commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '../../../commonInfra/contentActions/MultipleItemResult';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { AdminProfileErrorCode } from '../../../commonInfra/utils/ErrorCodes';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { NotificationHelper } from '../../components/helpers/NotificationHelper';
import { ISiteUserInfo } from '../../users/models/ISiteUserInfo';
import { IUserInfo } from '../../users/models/IUserInfo';

/** Notification for admin profile assignment (set/remove). Users set must be keyed by LUID to match API error ids. */
export class AdminProfileUpdateActionNotification {

  public static readonly instance = new AdminProfileUpdateActionNotification();

  public notify(result: IResult, users: Set<IUserInfo>): void {
    const successfulName = this.getSuccessfulNameByLuid(users, result);
    const failingName = this.getFailingNameByLuid(users, result);

    const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
      new MultipleItemResult(users, result),
      {
        describeSuccessSummary: (successCount: number) => {
          if (successCount === 1) {
            return Messages.updateAdminProfileSuccessSingle({
              USER_NAME: L10n.elideIfLong(successfulName)
            });
          }
          return Messages.updateAdminProfileSuccessMultiple({
            SUCCESS_COUNT_LOC: L10n.localizedCount(successCount)
          });
        },
        describeErrorSummary: (errorCount: number) => {
          if (errorCount === 1) {
            return Messages.updateAdminProfileErrorSingle({
              USER_NAME: L10n.elideIfLong(failingName)
            });
          }
          return Messages.updateAdminProfileErrorMultiple({
            ERROR_COUNT_LOC: L10n.localizedCount(errorCount)
          });
        },
        describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
          return {
            itemName: errorItem.item.username,
            itemUrl: NotificationHelper.getUrlForUser(errorItem.item),
            errorMessage: this.errorMessageFor(errorItem)
          };
        }
      }
    );

    ContentActionNotification.displayItemizedToastsForResult(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<IUserInfo>): string {
    const code = Number(errorItem.error.code);
    const serverMessage = errorItem.error.message;
    switch (code) {
      case AdminProfileErrorCode.SiteRoleNotAllowedForAdminProfile:
        return Messages.adminProfileSiteRoleNotAllowed();
      case AdminProfileErrorCode.AdminProfileNotFound:
        return Messages.adminProfileNotFound();
      default:
        return serverMessage || Messages.errorUnknown();
    }
  }

  /** Name for success summary; matches errors by user LUID (result.error.id). */
  private getSuccessfulNameByLuid(users: Set<IUserInfo>, result: IResult): string {
    const item = this.getSuccessfulItemByLuid(users, result);
    return item ? NotificationHelper.nameFor(item) : '';
  }

  /** Name for error summary; matches errors by user LUID. */
  private getFailingNameByLuid(users: Set<IUserInfo>, result: IResult): string {
    const item = this.getFailingItemByLuid(users, result);
    return item ? NotificationHelper.nameFor(item) : '';
  }

  private getSuccessfulItemByLuid(users: Set<IUserInfo>, result: IResult): IUserInfo | undefined {
    const errorLuids = result?.errors ? _.pluck(result.errors, 'id') : [];
    return users.find((item: IUserInfo) => !_.contains(errorLuids, (item as ISiteUserInfo).luid));
  }

  private getFailingItemByLuid(users: Set<IUserInfo>, result: IResult): IUserInfo | undefined {
    const errorLuids = result?.errors ? _.pluck(result.errors, 'id') : [];
    return users.find((item: IUserInfo) => _.contains(errorLuids, (item as ISiteUserInfo).luid));
  }
}

export default AdminProfileUpdateActionNotification.instance;
